import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, Button, message, Radio } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  CREATE_ADDRESS_WHITELIST,
  ANALYZE_ADDRESS_SIMPLE,
  AnalyzeAddressSimpleResponse,
  CreateAddressWhitelistInput,
} from '../graphql/addressWhitelistGraphql';

interface AddAddressWhitelistDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ValidationState {
  isValid: boolean;
  reason?: string;
}

type StandardizationMode = 'auto' | 'manual';

const AddAddressWhitelistDrawer: React.FC<AddAddressWhitelistDrawerProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [validation, setValidation] = useState<ValidationState | null>(null);
  const [mode, setMode] = useState<StandardizationMode>('auto');

  const streetAddress = Form.useWatch('originalStreetAddress', form);
  const building = Form.useWatch('originalBuilding', form);
  const city = Form.useWatch('originalCity', form);
  const state = Form.useWatch('originalState', form);
  const zipcode = Form.useWatch('originalZipcode', form);

  useEffect(() => {
    const s = streetAddress?.trim() ?? '';
    const b = building?.trim() ?? '';
    const c = city?.trim() ?? '';
    const st = state?.trim() ?? '';
    const z = zipcode?.trim() ?? '';
    const streetPart = b ? `${s} ${b}` : s;
    form.setFieldValue(
      'originalFullAddress',
      streetPart || c || st || z ? `${streetPart}, ${c}, ${st} ${z}` : ''
    );
    if (mode === 'auto') {
      form.setFieldValue('standardizedFullAddress', undefined);
      form.setFieldValue('addressId', undefined);
      setValidation(null);
    }
  }, [streetAddress, building, city, state, zipcode, form, mode]);

  const [analyzeAddress, { loading: validating }] = useLazyQuery<AnalyzeAddressSimpleResponse>(
    ANALYZE_ADDRESS_SIMPLE,
    { fetchPolicy: 'network-only' }
  );

  const canValidate =
    !!streetAddress?.trim() &&
    !!city?.trim() &&
    !!state?.trim() &&
    !!zipcode?.trim();

  const handleModeChange = (e: RadioChangeEvent) => {
    const newMode: StandardizationMode = e.target.value;
    setMode(newMode);
    form.setFieldValue('standardizedFullAddress', undefined);
    form.setFieldValue('addressId', undefined);
    setValidation(null);
  };

  const handleValidate = async () => {
    const { data, error } = await analyzeAddress({
      variables: {
        streetAddress: streetAddress!.trim(),
        unit: building?.trim() || null,
        city: city!.trim(),
        state: state!.trim(),
        zipCode: zipcode!.trim(),
        forceExternalApiMatch: true
      },
    });
    if (error) {
      form.setFieldValue('standardizedFullAddress', undefined);
      form.setFieldValue('addressId', undefined);
      setValidation({ isValid: false, reason: error.message });
      return;
    }
    const result = data?.analyzeAddressSimple;
    if (!result) {
      form.setFieldValue('standardizedFullAddress', undefined);
      form.setFieldValue('addressId', undefined);
      setValidation({ isValid: false, reason: t('mapTool.addressWhitelist.drawer.validateNoResult') as string });
      return;
    }
    if (result.isValid && result.entityComponents) {
      const nc = result.entityComponents;
      const addressLine2 = nc.addressLine2 ? ` ${nc.addressLine2}` : '';
      const standardAddress = `${nc.addressLine1}${addressLine2}, ${nc.cityName}, ${nc.stateAbbreviation} ${nc.zipcode}`;
      form.setFieldValue('standardizedFullAddress', standardAddress);
      form.setFieldValue('addressId', nc.idLog ?? undefined);
      setValidation({ isValid: true });
    } else {
      form.setFieldValue('standardizedFullAddress', undefined);
      form.setFieldValue('addressId', undefined);
      setValidation({ isValid: false, reason: result.validationReason ?? undefined });
    }
  };

  const [createWhitelist, { loading }] = useMutation(CREATE_ADDRESS_WHITELIST, {
    onCompleted: () => {
      message.success(t('mapTool.addressWhitelist.drawer.messageAddSuccess') as string);
      form.resetFields();
      setValidation(null);
      setMode('auto');
      onSuccess();
      onClose();
    },
    onError: (err) => {
      message.error(
        err.message || (t('mapTool.addressWhitelist.drawer.messageAddFailed') as string)
      );
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (mode === 'auto' && !validation?.isValid) {
        message.warning(t('mapTool.addressWhitelist.drawer.warnValidateFirst') as string);
        return;
      }
      const input: CreateAddressWhitelistInput = {
        originalZipcode: values.originalZipcode.trim(),
        originalState: values.originalState.trim(),
        originalCity: values.originalCity.trim(),
        originalDistrict: null,
        originalStreetAddress: values.originalStreetAddress.trim(),
        originalBuilding: values.originalBuilding?.trim() || null,
        originalFullAddress: values.originalFullAddress,
        addressId: values.addressId ? Number(values.addressId) : null,
        addressSource: null,
        standardizedFullAddress: values.standardizedFullAddress ?? null,
        isEnabled: true,
        remark: values.remark?.trim() || null,
      };
      await createWhitelist({ variables: { input } });
    } catch {
      // validation error, do nothing
    }
  };

  const handleClose = () => {
    form.resetFields();
    setValidation(null);
    setMode('auto');
    onClose();
  };

  return (
    <Drawer
      title={t('mapTool.addressWhitelist.drawer.title') as string}
      width={560}
      open={visible}
      onClose={handleClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose}>{t('components.button.cancel')}</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {t('mapTool.addressWhitelist.drawer.buttonSave')}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="originalStreetAddress"
          label={t('mapTool.addressWhitelist.drawer.labelStreetAddress')}
          rules={[{ required: true, message: t('mapTool.addressWhitelist.drawer.ruleStreetAddressRequired') as string }]}
        >
          <Input
            placeholder={t('mapTool.addressWhitelist.drawer.placeholderStreetAddress') as string}
            maxLength={255}
          />
        </Form.Item>
        <Form.Item
          name="originalBuilding"
          label={t('mapTool.addressWhitelist.drawer.labelAddressLine2')}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          name="originalCity"
          label={t('mapTool.addressWhitelist.drawer.labelCity')}
          rules={[{ required: true, message: t('mapTool.addressWhitelist.drawer.ruleCityRequired') as string }]}
        >
          <Input
            placeholder={t('mapTool.addressWhitelist.drawer.placeholderCity') as string}
            maxLength={200}
          />
        </Form.Item>
        <Form.Item
          name="originalState"
          label={t('mapTool.addressWhitelist.drawer.labelState')}
          rules={[{ required: true, message: t('mapTool.addressWhitelist.drawer.ruleStateRequired') as string }]}
        >
          <Input
            placeholder={t('mapTool.addressWhitelist.drawer.placeholderState') as string}
            maxLength={50}
          />
        </Form.Item>
        <Form.Item
          name="originalZipcode"
          label={t('mapTool.addressWhitelist.drawer.labelZipcode')}
          rules={[{ required: true, message: t('mapTool.addressWhitelist.drawer.ruleZipcodeRequired') as string }]}
        >
          <Input
            placeholder={t('mapTool.addressWhitelist.drawer.placeholderZipcode') as string}
            maxLength={10}
          />
        </Form.Item>
        <Form.Item
          name="originalFullAddress"
          label={t('mapTool.addressWhitelist.drawer.labelFullAddress')}
        >
          <Input readOnly className="bg-gray-50 cursor-default" />
        </Form.Item>

        <Form.Item label={t('mapTool.addressWhitelist.drawer.labelStandardizationMode')}>
          <Radio.Group value={mode} onChange={handleModeChange}>
            <Radio value="auto">{t('mapTool.addressWhitelist.drawer.radioAutoMode')}</Radio>
            <Radio value="manual">{t('mapTool.addressWhitelist.drawer.radioManualMode')}</Radio>
          </Radio.Group>
        </Form.Item>

        {mode === 'auto' && (
          <div className="mb-4">
            <Button
              disabled={!canValidate}
              loading={validating}
              onClick={handleValidate}
            >
              {t('mapTool.addressWhitelist.drawer.buttonValidate')}
            </Button>
            {validation && !validation.isValid && (
              <div className="mt-2 px-3 py-2 rounded text-sm bg-red-50 text-red-600 border border-red-200">
                {`${t('mapTool.addressWhitelist.drawer.validateFailed')}: ${validation.reason ?? ''}`}
              </div>
            )}
          </div>
        )}

        <Form.Item
          name="standardizedFullAddress"
          label={t('mapTool.addressWhitelist.drawer.labelStandardizedFullAddress')}
          rules={[{ required: true, message: t('mapTool.addressWhitelist.drawer.ruleStandardizedFullAddressRequired') as string }]}
        >
          {mode === 'auto' ? (
            <Input readOnly className="bg-gray-50 cursor-default" />
          ) : (
            <Input maxLength={500} />
          )}
        </Form.Item>
        <Form.Item
          name="addressId"
          label={t('mapTool.addressWhitelist.drawer.labelAddressId')}
          rules={[{ required: true, message: t('mapTool.addressWhitelist.drawer.ruleAddressIdRequired') as string }]}
        >
          {mode === 'auto' ? (
            <Input readOnly className="bg-gray-50 cursor-default" />
          ) : (
            <Input maxLength={50} />
          )}
        </Form.Item>

        <Form.Item
          name="remark"
          label={t('mapTool.addressWhitelist.drawer.labelRemark')}
        >
          <Input.TextArea
            placeholder={t('mapTool.addressWhitelist.drawer.placeholderRemark') as string}
            maxLength={255}
            rows={3}
          />
        </Form.Item>
        <Form.Item name="isEnabled" hidden initialValue={true}>
          <Input />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddAddressWhitelistDrawer;